import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import Input from '../ui/Input';
import Button from '../ui/Button';
import { PromotionField, PromotionFieldType } from '../../types/promotion';

interface PromotionFieldsBuilderProps {
  fields: PromotionField[];
  onChange: (fields: PromotionField[]) => void;
}

const FIELD_TYPES: PromotionFieldType[] = ['text', 'email', 'tel'];

const blankField = (): PromotionField => ({
  name: '',
  label: '',
  type: 'text',
  required: true,
});

const PromotionFieldsBuilder: React.FC<PromotionFieldsBuilderProps> = ({ fields, onChange }) => {
  const update = (idx: number, patch: Partial<PromotionField>) => {
    onChange(fields.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  };
  const remove = (idx: number) => onChange(fields.filter((_, i) => i !== idx));
  const add = () => onChange([...fields, blankField()]);

  return (
    <div className="space-y-3">
      {fields.length === 0 && (
        <div className="text-sm text-gray-500 dark:text-gray-400 italic">
          No fields configured. Add at least one to capture data.
        </div>
      )}
      {fields.map((field, idx) => (
        <div
          key={idx}
          className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-900/30"
        >
          <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-start">
            <div className="md:col-span-3">
              <label className="block text-[0.65rem] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Name (slug)</label>
              <Input
                value={field.name}
                onChange={(e) => update(idx, { name: e.target.value.replace(/[^a-zA-Z0-9_]/g, '') })}
                placeholder="email"
                size="sm"
              />
            </div>
            <div className="md:col-span-3">
              <label className="block text-[0.65rem] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Label</label>
              <Input
                value={field.label}
                onChange={(e) => update(idx, { label: e.target.value })}
                placeholder="Email"
                size="sm"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-[0.65rem] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Type</label>
              <select
                value={field.type}
                onChange={(e) => update(idx, { type: e.target.value as PromotionFieldType })}
                className="h-9 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 px-2 focus:outline-none focus:ring-2 focus:ring-zebu-500"
              >
                {FIELD_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-[0.65rem] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Max length</label>
              <Input
                type="number"
                value={field.max_length ?? ''}
                onChange={(e) => update(idx, { max_length: e.target.value ? Number(e.target.value) : undefined })}
                size="sm"
                min={1}
              />
            </div>
            <div className="md:col-span-1 flex items-center gap-2 pt-5">
              <input
                type="checkbox"
                checked={field.required}
                onChange={(e) => update(idx, { required: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-zebu-600 focus:ring-zebu-500"
              />
              <span className="text-xs text-gray-600 dark:text-gray-400">Req.</span>
            </div>
            <div className="md:col-span-1 flex items-center justify-end pt-4">
              <button
                type="button"
                onClick={() => remove(idx)}
                className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                aria-label="Remove field"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="mt-2">
            <label className="block text-[0.65rem] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Regex (optional)</label>
            <Input
              value={field.regex ?? ''}
              onChange={(e) => update(idx, { regex: e.target.value || undefined })}
              placeholder="^[A-Z]{2,}$"
              size="sm"
            />
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" leftIcon={<Plus className="h-4 w-4" />} onClick={add}>
        Add field
      </Button>
    </div>
  );
};

export default PromotionFieldsBuilder;
